import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Box, Heading, Text, Flex, Stack, HStack, VStack, Spacer, Divider, Icon, Button, Spinner, FormControl, InputGroup, InputLeftElement, Input, FormErrorMessage, Switch } from '@chakra-ui/react';
import DefaultAuth from './../layout/Default'
import { MdOutlineEmail, MdOutlineMailOutline } from 'react-icons/md';
import Logger from '../../../components/utils/logger'

const logger = new Logger('EmailSubscriptions');

function EmailSubscriptions() {
  const illustration = `${process.env.PUBLIC_URL}/assets/img/auth/droplla-side-banner.png`;

  const { register, handleSubmit, formState: { errors } } = useForm();
  const [isLoading, setIsLoading] = useState(false);

  const [token, setToken] = useState(null);
  const [address, setAddress] = useState(null);
  const [subscriptions, setSubscriptions] = useState(null);

  const onSubscribe = async (data) => {
    setIsLoading(true);
    try {
      const emailAddress = data.email;
      if (emailAddress !== null) {
        const response = await fetch(`/api/users/email/subscribe?emailAddress=${emailAddress}`, {
          method: 'GET',
        });
        const jsonData = await response.json();
        setToken(jsonData.emailAddressToken);
        setAddress(jsonData.emailAddress);
        setSubscriptions(jsonData.subscriptions);
      };
    } catch (error) {
      logger.error(error)
      setToken(null);
      setAddress(null);
      setSubscriptions(null);
    } finally {
      setIsLoading(false);
    }
  };

  const lookupSubscriptions = async (token) => {
    try {
      if (token === null) {
        setAddress(null);
        setSubscriptions(null);
      } else {
        const response = await fetch(`/api/users/email/subscriptions?emailAddressToken=${token}`, {
          method: 'GET',
        });
        const jsonData = await response.json();
        setToken(jsonData.emailAddressToken);
        setAddress(jsonData.emailAddress);
        setSubscriptions(jsonData.subscriptions);
      };
    } catch (error) {
      logger.error(error)
      setToken(null);
      setAddress(null);
      setSubscriptions(null);
    };
  };

  const updateSubscriptions = async (token, kind, subscribed) => {
    try {
      if (token === null) {
        setAddress(null);
        setSubscriptions(null);
      } else {
        const response = await fetch(`/api/users/email/subscriptions?emailAddressToken=${token}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            updates: (kind
              ? { [kind]: subscribed }
              : subscriptions.reduce((acc, sub) => {
                acc[sub.kind] = subscribed;
                return acc;
              }, {})
            )
          }),
        });

        if (response.ok) {
          const jsonData = await response.json();
          setToken(jsonData.emailAddressToken);
          setAddress(jsonData.emailAddress);
          setSubscriptions(jsonData.subscriptions);
        } else {
          throw new Error(`Failed to fetch subscriptions: ${response.status}`);
        }
      };
    } catch (error) {
      logger.error(error)
    }
  };

  useEffect(() => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams(window.location.search);
      window.history.replaceState({}, document.title, window.location.href.split('?')[0]);
      const token = params.get('emailAddressToken');
      if (token) {
        setToken(token);
        const unsubscribeParam = params.get('unsubscribe');
        if (unsubscribeParam) {
          updateSubscriptions(token, unsubscribeParam, false);
        } else {
          lookupSubscriptions(token);
        }
      }
    } catch (error) {
      logger.error(error)
    } finally {
      setIsLoading(false);
    }
  }, []);

  const header = token === null ? "Subscribe" : "Subscriptions";

  return (
    <DefaultAuth illustrationBackground={illustration} image={illustration}>
      <Flex
        maxW={{ base: "100%", "md": "max-content" }}
        w='100%'
        mx={{ base: "auto", lg: "0px" }}
        me='auto'
        h='100%'
        alignItems='start'
        justifyContent='center'
        mb={{ base: "30px", md: "60px" }}
        px={{ base: "25px", md: "0px" }}

        flexDirection='column'>

        <Box
          borderWidth="1px"
          borderColor="gray.200"
          borderRadius="xl"
          p={4}
          ml={{ base: 0, lg: 0 }}
          mr={{ base: 0, lg: 0 }}
          mt={4}
          width="100%"
        >
          <Flex alignItems="flex-start" w={{ base: "100%", md: "420px" }} >
            <Stack direction="column" spacing={3} w="100%" >
              <Flex alignItems="center">
                <Icon as={MdOutlineEmail} mr={3} color="gray.400" boxSize={{ base: 8, md: 10 }} />

                <Heading color="gray.400" as="h2" size={{ base: "md", md: "lg" }}>
                  {header}
                </Heading>
              </Flex>
              <Divider mt={2} mb={2} />
              {token === null ? (
                <form onSubmit={handleSubmit(onSubscribe)}>
                  <VStack display="flex" align="stretch" spacing={4}>
                    <Box display="flex" alignContent="right" justifyContent="right">
                      <FormControl mt={.4} isInvalid={errors.email}>
                        <InputGroup>
                          <InputLeftElement pointerEvents="none">
                            <Icon as={MdOutlineMailOutline} color="secondaryGray.600" boxSize={5} />
                          </InputLeftElement>
                          <Input
                            variant='droplla'
                            type="email"
                            placeholder="Enter your email"
                            id='email'
                            {...register('email', {
                              required: 'Email is required',
                              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Invalid email address' }
                            })}
                            autoComplete="email"
                          />
                        </InputGroup>
                        <Box>
                          <FormErrorMessage fontSize="xs">{errors.email && errors.email.message}</FormErrorMessage>
                        </Box>
                      </FormControl>
                    </Box>
                    <Box display="flex" alignContent="right" justifyContent="right">
                      <Button variant="droplla" type="submit" isLoading={isLoading}>
                        Subscribe
                      </Button>
                    </Box>
                  </VStack>
                </form>
              ) : (
                (isLoading || subscriptions === null || subscriptions.size === 0) ? (
                  <Box display="flex" alignItems="center" justifyContent="center" height="100%" width="100%">
                    <Spinner marginTop={2} marginBottom={5} thickness="4px"
                      speed="0.75s"
                      emptyColor="gray.200"
                      color="blue.500"
                      size="xl" />
                  </Box>
                ) : (
                  <Box>
                    <Text fontSize="xl" mb={8}>{address}</Text>
                    {Object.values(subscriptions).map((value, _) => (
                      <HStack mb={10}>
                        <Switch
                          id={value.kind}
                          isChecked={value.subscribed}
                          isDisabled={!value.optional}
                          isReadOnly={!value.optional}
                          isLoading={isLoading}
                          onChange={(e) => updateSubscriptions(token, value.kind, e.target.checked)}
                        />
                        <Box>
                          <Text fontSize="lg">{value.kind}</Text>
                          <Text fontSize="md">{value.description}</Text>
                        </Box>
                      </HStack>
                    ))}
                    <Flex>
                      <Button variant="droplla" isLoading={isLoading} onClick={() => updateSubscriptions(token, null, true)}>Accept All</Button>
                      <Spacer />
                      <Button variant="droplla" isLoading={isLoading} onClick={() => updateSubscriptions(token, null, false)}>Reject All</Button>
                    </Flex>
                  </Box>
                )
              )}
            </Stack>
          </Flex>
        </Box>
      </Flex>
    </DefaultAuth>
  );
}

export default EmailSubscriptions;
